import { Tank } from 'app/agriculture/queries/tanks/types/Tank';
import { useNavigate } from 'react-router-dom';
import { ContentBox } from 'app/agriculture/components/ContentBox/ContentBox';
import { Actions } from 'app/agriculture/components/Actions/Actions';
import { InfoIcon, LocationIcon, TankIcon } from 'app/agriculture/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTank } from 'app/agriculture/queries/tanks/useTank';
import { FormDiscardChangesBlocker } from 'app/agriculture/forms/components/FormDiscardChangesBlocker/FormDiscardChangesBlocker';
import { useEntityUpdateMutation } from 'app/agriculture/mutations/entity/update/useEntityUpdateMutation';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import 'app/agriculture/routes/Tanks/TankEdit/components/TankEditForm.scss';
import { RenderedMapMarker } from 'app/agriculture/components/Map/RenderedMapMarker/RenderedMapMarker';
import { useState } from 'react';
import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { BannerContent } from 'app/agriculture/Banner/BannerContent';

type TankEditFields = Pick<Tank, 'name' | 'geometry'>;

export function TankEditForm({ tankId }: { tankId: string }) {
    const tank = useTank(tankId);
    const navigate = useNavigate();

    const formController = useForm<TankEditFields>({
        defaultValues: tank,
        mode: 'all',
        shouldFocusError: true,
    });

    const {
        register,
        reset,
        handleSubmit,
        formState: { errors, isDirty },
        setValue,
        getValues,
    } = formController;

    const { mutate, isPending } = useEntityUpdateMutation('Tank');

    const onSubmit: SubmitHandler<TankEditFields> = (formData) => {
        mutate(
            {
                ...tank,
                ...formData,
            },
            {
                onSuccess: () => {
                    reset({}, { keepValues: true });
                    navigate('../');
                },
                onError: () => {
                    reset({}, { keepValues: true });
                },
            },
        );
    };

    const [coordinates, setCoordinates] = useState<MapLocation>({
        latitude: getValues('geometry.coordinates.1'),
        longitude: getValues('geometry.coordinates.0'),
    });

    return (
        <ContentBox title="Edit Tank" Icon={TankIcon} className="TankEditForm" variant="md">
            <FormProvider {...formController}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormDiscardChangesBlocker />
                    <ContentBox.Section scrollable className="TankEditForm__fields">
                        <Input
                            label="Name"
                            {...register('name', {
                                required: 'Please enter a name for your tank',
                            })}
                            errorMessage={errors.name?.message}
                        />
                        <RenderedMapMarker
                            value={coordinates}
                            onChange={({ latitude, longitude }) => {
                                setValue('geometry.coordinates.1', latitude, {
                                    shouldDirty: true,
                                });
                                setValue('geometry.coordinates.0', longitude, {
                                    shouldDirty: true,
                                });
                                setCoordinates({ latitude, longitude });
                            }}
                        />
                        <BannerContent>
                            <>
                                <section className="banner-section">
                                    <LocationIcon color="#3098E8" size={18} />
                                    <h4>Set location tool</h4>
                                </section>
                                <section className="banner-section">
                                    <InfoIcon color="#3098E8" size={18} />
                                    <p>Left click to place a Tank</p>
                                </section>
                            </>
                        </BannerContent>

                        <Actions
                            sticky
                            primaryAction={{
                                text: 'Save tank changes',
                                type: 'submit',
                                isLoading: isPending,
                                disabled: !isDirty,
                            }}
                            backAction={{
                                text: 'Back',
                                onClick: () => {
                                    navigate(-1);
                                },
                            }}
                        />
                    </ContentBox.Section>
                </form>
            </FormProvider>
        </ContentBox>
    );
}
